/**
 * ActiveTripBanner — the in-progress trip surfaced at the top of the web trips page (the web
 * counterpart of the mobile active-trip banner). A full-width cover photo with the trip title + dates
 * on the left, and a frosted "Next up" panel on the right showing the next itinerary event (by the
 * user's local time) with a "View details" button that opens the trip's chat and the relevant guide.
 */
import type { ClientInstanceOf } from '@elara/client-core/firebase/types';
import type { ItineraryItem } from '@elara/client-core/models/ItineraryItemModel';
import type { TripModel } from '@elara/client-core/models/TripModel';
import type { PreviewLinkTarget } from '@elara/domain/models/schemas/preview-schemas';

import { ROUTES, tripPanelPath } from '@elara/client-core/constants';
import { cn } from '@elara/client-core/lib/utils';
import { ItineraryItemEventModel } from '@elara/client-core/models/ItineraryItemEventModel';
import { buildPreviewHref } from '@elara/domain/models/schemas/preview-schemas';

import { useFirstAvailableImage } from '#lib/use-first-available-image.js';
import { useItineraryState, useNavigate } from '#states/hooks.js';
import { Badge, Button } from '#ui';
import { ElaraIcon } from '#ui/icons/elara-icon.js';
import { Link } from '#views/Router.js';

/**
 * First image for the event, from its source item's related entity. A hotel event reads as the STAY,
 * so it prefers the stay's imagery (catalog hotel, else the Google Place attached to an
 * externally-booked stay) over the individual room's photo.
 */
function eventImageUrls(item: ItineraryItem | undefined): Array<string | undefined> {
  if (!item) return [];
  // A hotel event reads as the STAY, so its own imagery leads and the room's follows as the fallback —
  // the reverse of `coverImageUrls`, which a room row wants. Both are candidate LISTS rather than one
  // url, so a rotted photo steps to the next instead of leaving the banner blank.
  if (item.type === 'room') return [...item.stayImages.map((image) => image.url), ...item.coverImageUrls];
  return item.coverImageUrls;
}

/** Map the next event's source item to the guide preview to open ("View details"), or undefined. */
function guideTargetForItem(item: ItineraryItem | undefined): PreviewLinkTarget | undefined {
  if (!item) return undefined;
  if (item.type === 'flight' && item.referenceId) return { type: 'flight', id: item.referenceId };
  if (item.type === 'place' && item.referenceId) return { type: 'place', id: item.referenceId };
  if (item.type === 'room') {
    // The stay's guide — catalog hotel, else the Google Place attached to an externally-booked stay
    // with no catalog match — then the room itself for an Elara-sourced room with neither.
    const guideRef = item.guideRef;
    if (guideRef) return { type: guideRef.kind, id: guideRef.id };
    if (item.referenceId) return { type: 'room', id: item.referenceId };
  }
  return undefined;
}

export function ActiveTripBanner({ trip }: { trip: ClientInstanceOf<TripModel> }) {
  const { navigate } = useNavigate();
  const itineraryState = useItineraryState();

  const next = itineraryState.nextEventForTrip(trip.id);
  const event = next ? ItineraryItemEventModel.from(next.event) : undefined;
  // All-day events have no meaningful clock time — show the date instead. A hotel check-in reads as a
  // "From <time>" window rather than a point in time.
  const isCheckin = next?.event.type === 'hotel_checkin';
  const timeLabel = !event
    ? ''
    : event.formattedTime === 'All day'
      ? event.formattedDate
      : isCheckin
        ? `From ${event.formattedTime}`
        : event.formattedTime;
  // An `<img>` reports its own failures, so no probe — the returned `onError` is the same signal.
  const { src: eventImage, onError: onEventImageError } = useFirstAvailableImage(eventImageUrls(next?.item));
  const hasCover = Boolean(trip.coverImage);

  const handleViewDetails = () => {
    // Open the active trip's chat and the relevant guide (carried in the hash) in one navigation, so
    // the trip page opens with the preview already resolved.
    const target = guideTargetForItem(next?.item);
    navigate(`${tripPanelPath(trip.id, 'chat')}${target ? buildPreviewHref(target) : ''}`);
  };

  return (
    <div
      className={cn(
        'relative w-full overflow-hidden rounded-lg shadow-none transition-[shadow,translate] duration-200 md:hover:shadow-md md:hover:-translate-y-[3px]',
        hasCover ? 'bg-background-tertiary' : 'bg-gradient-to-br from-background-tertiary to-background-quaternary',
      )}
    >
      {hasCover && (
        <div
          className="pointer-events-none absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${trip.coverImage})` }}
          aria-hidden
        />
      )}
      {/* Progressive blur: a bottom band whose backdrop-blur is faded in by a gradient mask (clear at
          the top → opaque at the bottom), so the title stays readable on busy photos. */}
      {hasCover && (
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-3/5 backdrop-blur-md [-webkit-backdrop-filter:blur(24px)] [mask-image:linear-gradient(to_bottom,transparent,black)] [-webkit-mask-image:linear-gradient(to_bottom,transparent,black)]"
          aria-hidden
        />
      )}
      {/* Scrim under the title so white text stays legible over the photo (cover only). */}
      {hasCover && (
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent"
          aria-hidden
        />
      )}

      <div className="relative flex flex-col md:flex-row min-h-80">
        {/* Photo side — links to the trip overview. */}
        <Link
          to={ROUTES.TRIP}
          params={{ id: trip.id }}
          aria-label={trip.displayName}
          className="flex flex-1 flex-col justify-between gap-6 p-4 md:p-8 min-h-90"
        >
          <Badge variant="success" className="self-start">
            Active trip
          </Badge>
          <div className="flex flex-col gap-1">
            <h2
              className={cn(
                'font-display font-light text-2xl md:text-3xl',
                hasCover ? 'text-white [text-shadow:0_1px_3px_rgb(0_0_0_/_0.35)]' : 'text-foreground',
              )}
            >
              {trip.displayName}
            </h2>
            {trip.formattedDateRange && (
              <p
                className={
                  hasCover ? 'text-white/90 [text-shadow:0_1px_3px_rgb(0_0_0_/_0.35)]' : 'text-muted-foreground'
                }
              >
                {trip.formattedDateRange}
              </p>
            )}
          </div>
        </Link>

        {/* Frosted "Next up" panel. */}
        <div
          className={cn(
            'flex shrink-0 flex-col justify-between gap-4 p-4 md:p-6 md:w-90',
            'transition-colors cursor-pointer',
            // Over a photo: dark frosted glass + white text. Without one: a themed surface so it reads as
            // a panel on the light placeholder rather than dark glass floating on nothing.
            hasCover
              ? 'backdrop-blur-sm bg-camel-950/30 hover:bg-camel-950/45 text-white'
              : 'bg-background-tertiary hover:bg-background-tertiary text-foreground border border-border md:border-0',
          )}
          onClick={handleViewDetails}
        >
          {next && event ? (
            <div className="flex-1 flex items-center md:items-start md:flex-col gap-3">
              <div className="order-2 md:order-1">
                <span className="text-2xs uppercase tracking-wide font-medium opacity-70">Next up</span>
                <h3 className="font-display">{next.event.title}</h3>
                {timeLabel && <p className="text-sm opacity-90">{timeLabel}</p>}
              </div>
              {eventImage && (
                <img
                  key={eventImage}
                  src={eventImage}
                  onError={onEventImageError}
                  alt={next.event.title}
                  className="order-1 md:order-2 aspect-square md:aspect-video w-16 md:w-full h-full rounded-sm object-cover"
                />
              )}
            </div>
          ) : (
            <div className="flex-1 flex flex-col justify-between gap-3">
              <div className="space-y-3">
                <span className="text-2xs uppercase tracking-wide font-medium opacity-70">Next up</span>
                <div className="text-pretty">
                  There’s nothing left on your itinerary for today. Ask Elara to find something.
                </div>
              </div>
              <Button
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(tripPanelPath(trip.id, 'chat'));
                }}
              >
                <ElaraIcon />
                Chat with Elara
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
